import { useState, useEffect, useRef } from 'react'
import { Trophy, LogIn, UserPlus, User as UserIcon, Moon, Sun, Menu, X, CheckCircle, AlertTriangle, Mail } from 'lucide-react'
import TournamentView from './components/TournamentView'
import RegistrationModal from './components/RegistrationModal'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { getTheme } from './themes'
import { db } from './config/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { 
  initGA, 
  trackSignUp, 
  trackSignIn, 
  trackProfileUpdate,
  trackThemeToggle,
  trackAuthModalAction,
  setUserProperties,
  trackSessionQuality,
  trackEmailVerification,
  trackSignOut as trackSignOutEvent,
  trackError
} from './config/analytics'
import './index.css'

const TOURNAMENT_DATA_URL = '/results.json'

function AppContent() {
  const { currentUser, userProfile, signUp, signIn, signInWithGoogle, signUpWithGoogle, signOut, updateUserProfile, resendVerificationEmail } = useAuth()
  const [tournamentData, setTournamentData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [registrationCounts, setRegistrationCounts] = useState({})
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [authMode, setAuthMode] = useState('signin')
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false)
  const [profileUpdateError, setProfileUpdateError] = useState('')
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [showEmailVerificationNotice, setShowEmailVerificationNotice] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [showEmailSignup, setShowEmailSignup] = useState(false)
  const [showVerificationBanner, setShowVerificationBanner] = useState(true)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState('')
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false
  })
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  })
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    duprId: '',
    duprRating: ''
  })
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [categoryCache, setCategoryCache] = useState({})
  const [currentCategoryId, setCurrentCategoryId] = useState(null)
  const currentCategoryIdRef = useRef(null)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('tournament-viewer-dark-mode')
    return saved ? saved === 'true' : true // Default to dark mode
  })
  const colorTheme = getTheme()
  
  // Dynamic theme classes based on dark/light mode - Premium Professional Design
  const themeClasses = {
    bg: isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#fafafa]',
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    textMuted: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    textSubtle: isDarkMode ? 'text-gray-500' : 'text-gray-400',
    glass: isDarkMode 
      ? 'bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-2xl border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4)]' 
      : 'bg-white/90 backdrop-blur-2xl border border-gray-200/80 shadow-[0_8px_32px_rgba(0,0,0,0.08)]',
    glassDark: isDarkMode 
      ? 'bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-2xl border border-white/[0.06]' 
      : 'bg-white/95 backdrop-blur-2xl border border-gray-100',
    cardBg: isDarkMode 
      ? 'bg-gradient-to-br from-white/[0.06] to-white/[0.02] border-white/[0.08]' 
      : 'bg-white border-gray-200',
    cardHover: isDarkMode
      ? 'hover:from-white/[0.1] hover:to-white/[0.04] hover:border-emerald-500/30 hover:shadow-[0_8px_40px_rgba(16,185,129,0.15)]'
      : 'hover:bg-gray-50 hover:border-emerald-500/40 hover:shadow-[0_8px_40px_rgba(16,185,129,0.1)]',
    gradient: isDarkMode ? 'from-[#0a0a0a] via-[#111111] to-[#0a0a0a]' : 'from-[#fafafa] via-white to-[#f5f5f5]',
    border: isDarkMode ? 'border-white/[0.08]' : 'border-gray-200',
    hoverBg: isDarkMode ? 'hover:bg-white/[0.08]' : 'hover:bg-gray-50',
    accent: isDarkMode ? 'from-emerald-500 to-teal-400' : 'from-emerald-500 to-emerald-600',
    headerBg: isDarkMode 
      ? 'bg-[#0a0a0a]/80 backdrop-blur-2xl border-b border-white/[0.06]' 
      : 'bg-white/80 backdrop-blur-2xl border-b border-gray-200/80 shadow-sm',
    inputBg: isDarkMode ? 'bg-white/[0.06]' : 'bg-white',
    inputBorder: isDarkMode ? 'border-white/[0.12]' : 'border-gray-300',
    inputPlaceholder: isDarkMode ? 'placeholder-gray-500' : 'placeholder-gray-400'
  }

  // Fetch tournament data function (extracted to be reusable)
  const fetchTournamentData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true)
      }
      setError(null)
      
      // Add cache-busting parameter to ensure fresh data
      const timestamp = new Date().getTime()
      const response = await fetch(`${TOURNAMENT_DATA_URL}?t=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tournament data: ${response.status} ${response.statusText}`)
      }
      
      const json = await response.json()
      // Validate JSON structure
      if (!json.tournament || !json.categories) {
        throw new Error('Invalid tournament data format')
      }
      
      setTournamentData(prev => {
        if (prev?.exportedAt === json.exportedAt) return prev // data unchanged, skip re-render
        return json
      })
      if (currentCategoryIdRef.current) fetchCategoryData(currentCategoryIdRef.current)
      if (isInitialLoad) {
        setLoading(false)
      }
    } catch (err) {
      if (isInitialLoad) {
        setError(err.message || 'Failed to load tournament data')
        setLoading(false)
      }
      console.error('Error fetching tournament data:', err)
    }
  }

  const fetchCategoryData = async (categoryId) => {
    if (!categoryId) return
    try {
      const ts = new Date().getTime()
      const res = await fetch(`/results-${categoryId}.json?t=${ts}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      })
      if (!res.ok) return
      const data = await res.json()
      setCategoryCache(prev => {
        if (prev[categoryId]?.exportedAt === data.exportedAt) return prev // unchanged
        return { ...prev, [categoryId]: data }
      })
    } catch (err) {
      console.error('Error fetching category data:', err)
    }
  }

  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA()
    
    // Track session quality on page unload
    const handleBeforeUnload = () => trackSessionQuality()
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Update document title and favicon when tournament data loads
  useEffect(() => {
    if (tournamentData?.tournament) {
      const { name, logoUrl } = tournamentData.tournament
      
      // Update document title
      if (name) {
        document.title = `${name} - Powered by Sports Control Hub`
      }
      
      // Update favicon if logo URL is available
      if (logoUrl) {
        let link = document.querySelector("link[rel~='icon']")
        if (!link) {
          link = document.createElement('link')
          link.rel = 'icon'
          document.head.appendChild(link)
        }
        link.href = logoUrl
      }
    }
  }, [tournamentData])

  // Password validation helper
  const validatePassword = (password) => {
    return {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password)
    }
  }

  const isPasswordValid = (validation) => {
    return validation.minLength && validation.hasUpperCase && validation.hasLowerCase && validation.hasNumber
  }

  const validateMobileNumber = (number) => {
    if (!number || !number.trim()) {
      return true // Empty is valid (optional field)
    }
    
    // Remove all spaces, dashes, and parentheses
    const cleaned = number.replace(/[\s\-()]/g, '')
    
    // Philippine format with +63: Must be exactly 13 characters (+63 + 10 digits)
    if (cleaned.startsWith('+63')) {
      return /^\+63\d{10}$/.test(cleaned)
    }
    
    // Philippine local format starting with 09: Must be exactly 11 digits
    if (cleaned.startsWith('09')) {
      return /^09\d{9}$/.test(cleaned)
    }
    
    // International format (non-Philippine): + followed by country code and number
    // Require minimum 10 digits total (country code + number)
    if (cleaned.startsWith('+')) {
      return /^\+\d{10,15}$/.test(cleaned)
    }
    
    // Other local formats: Require exactly 10-11 digits (common for most countries)
    return /^\d{10,11}$/.test(cleaned)
  }

  // Authentication handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Real-time password validation for signup mode
    if (name === 'password' && authMode === 'signup') {
      setPasswordValidation(validatePassword(value))
    }
  }

  const handleEmailAuth = async (e) => {
    e.preventDefault()
    setAuthError('')
    setAuthLoading(true)

    try {
      if (authMode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setAuthError('Passwords do not match')
          setAuthLoading(false)
          return
        }
        const validation = validatePassword(formData.password)
        if (!isPasswordValid(validation)) {
          setAuthError('Password does not meet all requirements')
          setAuthLoading(false)
          return
        }
        // Create display name from first and last name
        const displayName = `${formData.firstName} ${formData.lastName}`.trim()
        await signUp({
          email: formData.email,
          password: formData.password,
          displayName: displayName,
          firstName: formData.firstName,
          lastName: formData.lastName
        })
        // Track sign-up event
        trackSignUp('email')
        // Set user properties
        setUserProperties({
          signup_method: 'email',
          user_type: 'player'
        })
        // Show email verification notice for non-social sign-up
        setVerificationEmail(formData.email)
        setShowEmailVerificationNotice(true)
        setShowAuthModal(false)
        setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' })
      } else {
        await signIn({
          email: formData.email,
          password: formData.password
        })
        // Track sign-in event
        trackSignIn('email')
        // Set user properties
        if (userProfile) {
          setUserProperties({
            user_type: userProfile.role || 'player',
            has_profile: true
          })
        }
        setShowAuthModal(false)
        setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' })
      }
    } catch (err) {
      setAuthError(err.message || 'Authentication failed')
      trackError('Auth Error', err.message || 'Authentication failed', authMode)
    } finally {
      setAuthLoading(false)
    }
  }

  const handleGoogleAuth = async () => {
    setAuthError('')
    setAuthLoading(true)
    try {
      if (authMode === 'signup') {
        await signUpWithGoogle()
        // Track Google sign-up event
        trackSignUp('google')
        // Set user properties
        setUserProperties({
          signup_method: 'google',
          user_type: 'player'
        })
      } else {
        await signInWithGoogle()
        // Track Google sign-in event
        trackSignIn('google')
        // Set user properties
        if (userProfile) {
          setUserProperties({
            user_type: userProfile.role || 'player',
            has_profile: true
          })
        }
      }
      setShowAuthModal(false)
    } catch (err) {
      setAuthError(err.message || 'Google authentication failed')
      trackError('Auth Error', err.message || 'Google authentication failed', 'google')
    } finally {
      setAuthLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      trackSignOutEvent()
      await signOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  const handleResendVerification = async () => {
    setResendLoading(true)
    setResendError('')
    setResendSuccess(false)
    
    try {
      await resendVerificationEmail()
      setResendSuccess(true)
      trackEmailVerification('Resend Success')
      // Hide success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (err) {
      setResendError(err.message || 'Failed to send verification email')
      trackEmailVerification('Resend Error', { label: err.message })
    } finally {
      setResendLoading(false)
    }
  }

  const handleProfileFormChange = (e) => {
    setProfileFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setProfileUpdateError('')
    setProfileUpdateLoading(true)

    try {
      // Validate phone number if provided
      if (profileFormData.phoneNumber && !validateMobileNumber(profileFormData.phoneNumber)) {
        setProfileUpdateError('Please enter a complete mobile number (e.g., +63 912 345 6789 or 09123456789)')
        setProfileUpdateLoading(false)
        return
      }

      const updates = {}
      if (profileFormData.firstName) updates.firstName = profileFormData.firstName
      if (profileFormData.lastName) updates.lastName = profileFormData.lastName
      if (profileFormData.phoneNumber) updates.phoneNumber = profileFormData.phoneNumber
      if (profileFormData.duprId) updates.duprId = profileFormData.duprId
      if (profileFormData.duprRating) updates.duprRating = parseFloat(profileFormData.duprRating)

      await updateUserProfile(updates)
      // Track profile update event
      trackProfileUpdate()
      setIsEditingProfile(false)
      setProfileUpdateError('')
    } catch (err) {
      setProfileUpdateError(err.message || 'Failed to update profile')
    } finally {
      setProfileUpdateLoading(false)
    }
  }

  // Save theme preference to localStorage and apply theme class to document
  useEffect(() => {
    localStorage.setItem('tournament-viewer-dark-mode', isDarkMode.toString())
    // Apply theme class for scrollbar styling
    document.documentElement.classList.remove('theme-dark', 'theme-light')
    document.documentElement.classList.add(isDarkMode ? 'theme-dark' : 'theme-light')
  }, [isDarkMode])

  // Set GA user properties when auth state changes
  useEffect(() => {
    if (currentUser) {
      setUserProperties({
        user_id: currentUser.uid,
        email_verified: currentUser.emailVerified ? 'true' : 'false',
        auth_provider: currentUser.providerData?.[0]?.providerId || 'unknown',
      })
    }
  }, [currentUser, currentUser?.emailVerified])

  // Populate profile form when opening modal
  useEffect(() => {
    if (showUserProfile && userProfile) {
      setProfileFormData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        phoneNumber: userProfile.phoneNumber || '',
        duprId: userProfile.duprId || '',
        duprRating: userProfile.duprRating || ''
      })
      setIsEditingProfile(false)
      setProfileUpdateError('')
    }
  }, [showUserProfile, userProfile])

  // Load data on component mount and set up auto-refresh
  useEffect(() => {
    // Initial load
    fetchTournamentData(true)

    // Set up auto-refresh every 10 seconds for live score updates
    const refreshInterval = setInterval(() => {
      fetchTournamentData(false)
    }, 10000) // 10000ms = 10 seconds

    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval)
  }, [])

  // Set up real-time listener for registration counter (only when registration is open)
  useEffect(() => {
    if (!tournamentData?.tournament?.id) {
      return
    }

    const tournamentStatus = tournamentData.tournament.status
    
    // Only listen for registration counts when status is registration_open
    if (tournamentStatus !== 'registration_open') {
      setRegistrationCounts({})
      return
    }

    const tournamentId = tournamentData.tournament.id
    const counterRef = doc(db, 'registration_counter', tournamentId)

    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(
      counterRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          setRegistrationCounts(data.categories || {})
        } else {
          // No counter document yet, set empty counts
          setRegistrationCounts({})
        }
      },
      (error) => {
        console.error('Error listening to registration counter:', error)
        // Set empty counts on error
        setRegistrationCounts({})
      }
    )

    // Cleanup listener on unmount or tournament change
    return () => unsubscribe()
  }, [tournamentData?.tournament?.id, tournamentData?.tournament?.status])

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.bg} flex items-center justify-center p-4 transition-colors duration-500`}>
        {/* Premium Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full blur-[100px] animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-blue-500/15 to-purple-500/15 rounded-full blur-[100px] animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-gradient-to-r from-emerald-400/10 to-cyan-400/10 rounded-full blur-[80px] animate-pulse-slow"></div>
        </div>
        
        <div className="text-center relative z-10">
          {/* Premium Loading Spinner */}
          <div className="relative mb-8">
            <div className={`w-24 h-24 ${themeClasses.glass} rounded-2xl flex items-center justify-center mx-auto`}>
              <Trophy className={`w-12 h-12 ${colorTheme.text.primary}`} />
            </div>
            <div className="absolute inset-0 w-24 h-24 mx-auto">
              <div className="absolute inset-0 rounded-2xl border-2 border-emerald-500/30 animate-ping"></div>
            </div>
          </div>
          <h2 className={`text-3xl font-bold mb-3 tracking-tight ${themeClasses.text}`}>Loading Tournament</h2>
          <p className={`${themeClasses.textMuted} text-lg`}>{colorTheme.ribbon} Fetching latest results</p>
          {/* Loading Bar */}
          <div className={`mt-6 w-48 h-1 mx-auto rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div className="h-full w-1/2 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full animate-loading-bar"></div>
          </div>
        </div>
      </div>
    )
  }

  if (tournamentData) {
    return (
      <>
        {/* Auth Header */}
        <div className="fixed top-4 right-0 left-0 z-50 pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 flex justify-end pointer-events-none">
            {/* Desktop View - Hidden on mobile */}
            <div className="hidden sm:flex gap-3 pointer-events-auto">
          {/* Dark/Light Mode Toggle - Hidden, defaulting to dark mode */}
          {/* <button
            onClick={() => {
              const newTheme = !isDarkMode ? 'dark' : 'light'
              setIsDarkMode(!isDarkMode)
              trackThemeToggle(newTheme)
            }}
            className={`w-11 h-11 flex items-center justify-center rounded-full ${themeClasses.glass} hover:scale-110 transition-all duration-300`}
            aria-label="Toggle theme"
          >
            {isDarkMode ? <Sun className={`w-5 h-5 ${themeClasses.text}`} /> : <Moon className={`w-5 h-5 ${themeClasses.text}`} />}
          </button> */}
          
          {currentUser ? (
            <button
              onClick={() => setShowUserProfile(true)}
              className={`${themeClasses.glass} p-1.5 rounded-full ${themeClasses.hoverBg} transition-all duration-300 hover:scale-110`}
              title={currentUser.displayName || currentUser.email}
            >
              {currentUser.photoURL ? (
                <img src={currentUser.photoURL} alt={currentUser.displayName} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                  <UserIcon className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          ) : (
            (tournamentData?.tournament?.status === 'registration_open' || tournamentData?.tournament?.status === 'registration_closed' || tournamentData?.tournament?.status === 'in-progress' || tournamentData?.tournament?.status === 'in_progress') && (
              <>
                <button
                  onClick={() => {
                    setAuthMode('signin')
                    setShowAuthModal(true)
                    trackAuthModalAction('Opened', 'signin')
                  }}
                  className={`${themeClasses.glass} px-4 py-2 rounded-lg font-semibold ${themeClasses.hoverBg} transition-all duration-300 flex items-center gap-2 ${themeClasses.text}`}
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setAuthMode('signup')
                    setShowAuthModal(true)
                    trackAuthModalAction('Opened', 'signup')
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 px-4 py-2 rounded-lg text-white font-semibold transition-all duration-300 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign Up
                </button>
              </>
            )
          )}
            </div>
            
            {/* Mobile Hamburger Menu */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`sm:hidden pointer-events-auto w-11 h-11 flex items-center justify-center rounded-full ${themeClasses.glass} hover:scale-110 transition-all duration-300`}
              aria-label="Menu"
            >
              {showMobileMenu ? <X className={`w-5 h-5 ${themeClasses.text}`} /> : <Menu className={`w-5 h-5 ${themeClasses.text}`} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="sm:hidden fixed top-20 right-4 z-50 pointer-events-auto">
            <div className={`${themeClasses.glass} rounded-2xl shadow-2xl border ${themeClasses.border} p-4 space-y-3 min-w-[200px]`}>
              {/* Theme Toggle - Hidden, defaulting to dark mode */}
              {/* <button
                onClick={() => {
                  const newTheme = !isDarkMode ? 'dark' : 'light'
                  setIsDarkMode(!isDarkMode)
                  trackThemeToggle(newTheme)
                  setShowMobileMenu(false)
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${themeClasses.hoverBg} transition-all duration-300 ${themeClasses.text}`}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                <span className="font-semibold">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </button> */}
              

              {currentUser ? (
                <button
                  onClick={() => {
                    setShowUserProfile(true)
                    setShowMobileMenu(false)
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${themeClasses.hoverBg} transition-all duration-300`}
                >
                  {currentUser.photoURL ? (
                    <img src={currentUser.photoURL} alt={currentUser.displayName} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className={`font-semibold ${themeClasses.text} truncate`}>{currentUser.displayName || 'Profile'}</span>
                </button>
              ) : (
                (tournamentData?.tournament?.status === 'registration_open' || tournamentData?.tournament?.status === 'registration_closed' || tournamentData?.tournament?.status === 'in-progress' || tournamentData?.tournament?.status === 'in_progress') && (
                  <>
                    <button
                      onClick={() => {
                        setAuthMode('signin')
                        setShowAuthModal(true)
                        setShowMobileMenu(false)
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${themeClasses.hoverBg} transition-all duration-300 ${themeClasses.text}`}
                    >
                      <LogIn className="w-4 h-4" />
                      <span className="font-semibold">Sign In</span>
                    </button>
                    <button
                      onClick={() => {
                        setAuthMode('signup')
                        setShowAuthModal(true)
                        setShowMobileMenu(false)
                      }}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 px-4 py-3 rounded-lg text-white font-semibold transition-all duration-300 flex items-center gap-3"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        )}

        {/* Email Verification Banner - Always Visible */}
        {currentUser && !currentUser.emailVerified && showVerificationBanner && (
          <div className="fixed top-20 left-0 right-0 z-40 animate-in slide-in-from-top duration-300">
            <div className="max-w-7xl mx-auto px-4">
              <div className={`${isDarkMode ? 'bg-amber-500/20 border-amber-500/40' : 'bg-amber-50 border-amber-300'} border-2 rounded-2xl shadow-2xl backdrop-blur-sm`}>
                <div className="p-4 sm:p-5">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${isDarkMode ? 'bg-amber-500/30' : 'bg-amber-100'} flex items-center justify-center`}>
                      <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-900'}`}>
                          ⚠️ Email Verification Required
                        </h3>
                        <button
                          onClick={() => {
                            setShowVerificationBanner(false)
                            trackEmailVerification('Banner Dismissed')
                          }}
                          className={`flex-shrink-0 p-1 rounded-lg ${isDarkMode ? 'hover:bg-amber-500/20 text-amber-400' : 'hover:bg-amber-200 text-amber-700'} transition-colors`}
                          aria-label="Dismiss"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      
                      <p className={`text-sm sm:text-base mb-3 ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                        Please verify your email to access <strong>brackets, schedule, and live scores</strong>.
                      </p>
                      
                      <div className={`text-xs sm:text-sm mb-3 ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'} flex items-center gap-2`}>
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{currentUser.email}</span>
                      </div>
                      
                      {/* Resend Button and Status */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <button
                          onClick={handleResendVerification}
                          disabled={resendLoading || resendSuccess}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                            resendSuccess
                              ? 'bg-emerald-500 text-white cursor-default'
                              : isDarkMode
                              ? 'bg-amber-500 hover:bg-amber-600 text-white'
                              : 'bg-amber-600 hover:bg-amber-700 text-white'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {resendLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                              Sending...
                            </>
                          ) : resendSuccess ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Email Sent!
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4" />
                              Resend Verification Email
                            </>
                          )}
                        </button>
                        
                        {resendSuccess && (
                          <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'} font-medium`}>
                            Check your inbox and spam folder
                          </p>
                        )}
                        
                        {resendError && (
                          <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-red-400' : 'text-red-700'} font-medium`}>
                            {resendError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Profile Modal */}
        {showUserProfile && currentUser && (
          <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/70' : 'bg-black/50'} backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200`}>
            <div className={`${themeClasses.bg} rounded-2xl shadow-2xl max-w-sm w-full max-h-[90vh] overflow-auto border ${themeClasses.border} animate-in zoom-in-95 fade-in duration-300`}>
              {/* Header */}
              <div className={`sticky top-0 ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-white via-gray-50 to-white'} border-b ${themeClasses.border} p-6 rounded-t-2xl backdrop-blur-xl`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' : 'bg-gradient-to-br from-emerald-50 to-teal-50'} flex items-center justify-center border ${isDarkMode ? 'border-emerald-500/30' : 'border-emerald-200'}`}>
                      <UserIcon className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    </div>
                    <h2 className={`text-2xl font-black ${themeClasses.text} tracking-tight`}>Profile</h2>
                  </div>
                  <button
                    onClick={() => setShowUserProfile(false)}
                    className={`p-2 ${themeClasses.hoverBg} rounded-xl transition-all duration-300 hover:scale-110 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                  >
                    <svg className={`w-5 h-5 ${themeClasses.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className={`${isDarkMode ? 'bg-white/5' : 'bg-gray-100/50'} rounded-xl p-4 border ${themeClasses.border}`}>
                  <div className="flex items-center gap-4">
                    {currentUser.photoURL ? (
                      <img src={currentUser.photoURL} alt={currentUser.displayName} className="w-14 h-14 rounded-xl border-2 border-emerald-500/30" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
                        <UserIcon className="w-7 h-7 text-emerald-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className={`text-lg font-bold ${themeClasses.text} truncate`}>{currentUser.displayName || 'User'}</h3>
                      <p className={`${themeClasses.textMuted} text-sm truncate`}>{currentUser.email}</p>
                      {currentUser.emailVerified && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs text-emerald-400">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Details */}
              <div className="p-4 space-y-3">
                {profileUpdateError && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                    {profileUpdateError}
                  </div>
                )}

                {/* Unverified Account Warning */}
                {!currentUser.emailVerified && (
                  <div className={`${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-300'} border rounded-lg p-4`}>
                    <div className="flex items-start gap-3 mb-3">
                      <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <h4 className={`text-sm font-bold ${isDarkMode ? 'text-amber-300' : 'text-amber-900'} mb-1`}>
                          Email Not Verified
                        </h4>
                        <p className={`text-xs ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                          Verify your email to access brackets, schedule, and live scores.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendLoading || resendSuccess}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                        resendSuccess
                          ? 'bg-emerald-500 text-white cursor-default'
                          : isDarkMode
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-amber-600 hover:bg-amber-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {resendLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : resendSuccess ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Email Sent!
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          Resend Verification Email
                        </>
                      )}
                    </button>
                    {resendSuccess && (
                      <p className={`text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'} mt-2 text-center`}>
                        Check your inbox and spam folder
                      </p>
                    )}
                    {resendError && (
                      <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-700'} mt-2 text-center`}>
                        {resendError}
                      </p>
                    )}
                  </div>
                )}

                {!isEditingProfile ? (
                  <>
                    {/* View Mode */}
                    <div>
                      <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Email</label>
                      <p className={`${themeClasses.text} font-medium`}>{currentUser.email}</p>
                    </div>

                    {(userProfile?.firstName || userProfile?.lastName) ? (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Display Name</label>
                        <p className={`${themeClasses.text} font-medium`}>
                          {`${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()}
                        </p>
                      </div>
                    ) : currentUser.displayName && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Display Name</label>
                        <p className={`${themeClasses.text} font-medium`}>{currentUser.displayName}</p>
                      </div>
                    )}

                    {userProfile?.firstName && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>First Name</label>
                        <p className={`${themeClasses.text} font-medium`}>{userProfile.firstName}</p>
                      </div>
                    )}

                    {userProfile?.lastName && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Last Name</label>
                        <p className={`${themeClasses.text} font-medium`}>{userProfile.lastName}</p>
                      </div>
                    )}

                    {userProfile?.phoneNumber && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Mobile Number</label>
                        <p className={`${themeClasses.text} font-medium`}>{userProfile.phoneNumber}</p>
                      </div>
                    )}

                    {userProfile?.duprId && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>DUPR ID</label>
                        <p className={`${themeClasses.text} font-medium`}>{userProfile.duprId}</p>
                      </div>
                    )}

                    {userProfile?.duprRating && (
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>DUPR Rating</label>
                        <p className={`${themeClasses.text} font-medium`}>{userProfile.duprRating}</p>
                      </div>
                    )}

                    <div>
                      <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Account Status</label>
                      <div className="flex items-center gap-2">
                        {currentUser.emailVerified ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className={`${themeClasses.text} font-medium`}>Verified</span>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span className={`${themeClasses.text} font-medium`}>Unverified</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className={`pt-3 border-t ${themeClasses.border} space-y-2`}>
                      <button
                        onClick={() => setIsEditingProfile(true)}
                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
                      >
                        Edit Profile
                      </button>
                      <button
                        onClick={() => {
                          handleSignOut()
                          setShowUserProfile(false)
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm"
                      >
                        Sign Out
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Edit Mode */}
                    <form onSubmit={handleUpdateProfile} className="space-y-4">
                      <div>
                        <label className={`block text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Email (Read-only)</label>
                        <p className={`${themeClasses.textMuted} font-medium ${themeClasses.cardBg} px-4 py-3 rounded-lg border ${themeClasses.border}`}>{currentUser.email}</p>
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={profileFormData.firstName}
                          onChange={handleProfileFormChange}
                          className={`w-full px-4 py-3 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                          placeholder="John"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={profileFormData.lastName}
                          onChange={handleProfileFormChange}
                          className={`w-full px-4 py-3 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                          placeholder="Doe"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>Mobile Number</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={profileFormData.phoneNumber}
                          onChange={handleProfileFormChange}
                          className={`w-full px-4 py-3 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                          placeholder="+63 912 345 6789"
                        />
                        {profileFormData.phoneNumber && !validateMobileNumber(profileFormData.phoneNumber) && (
                          <p className="text-xs text-red-400 mt-1">
                            Please enter a complete mobile number (e.g., +63 912 345 6789 or 09123456789)
                          </p>
                        )}
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>DUPR ID</label>
                        <input
                          type="text"
                          name="duprId"
                          value={profileFormData.duprId}
                          onChange={handleProfileFormChange}
                          className={`w-full px-4 py-3 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                          placeholder="123456"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>DUPR Rating</label>
                        <input
                          type="number"
                          step="0.01"
                          name="duprRating"
                          value={profileFormData.duprRating}
                          onChange={handleProfileFormChange}
                          className={`w-full px-4 py-3 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-lg ${themeClasses.text} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-300`}
                          placeholder="3.5"
                        />
                      </div>

                      <div className={`pt-4 border-t ${themeClasses.border} space-y-3`}>
                        <button
                          type="submit"
                          disabled={profileUpdateLoading}
                          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {profileUpdateLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingProfile(false)
                            setProfileUpdateError('')
                          }}
                          className={`w-full ${themeClasses.cardBg} ${themeClasses.hoverBg} border ${themeClasses.border} ${themeClasses.text} px-6 py-3 rounded-lg font-bold transition-colors`}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="glass-dark rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto border border-white/10">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b border-white/10 p-8 rounded-t-2xl backdrop-blur-xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                      {authMode === 'signin' ? (
                        <LogIn className="w-6 h-6 text-emerald-400" />
                      ) : (
                        <UserPlus className="w-6 h-6 text-emerald-400" />
                      )}
                    </div>
                    <div>
                      <h2 className={`${authMode === 'signin' ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} font-black text-white tracking-tight`}>
                        {authMode === 'signin' ? 'Sign In' : 'Join Sports Control Hub'}
                      </h2>
                      <p className="text-sm text-gray-400 mt-1">
                        {authMode === 'signin' ? 'Ready to compete? Let\'s get you in!' : 'Become part of our community'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAuthModal(false)}
                    className="p-2 hover:bg-white/10 rounded-xl transition-all duration-300 hover:scale-110"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Form */}
              <div className="p-6">
                {authError && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
                    {authError}
                  </div>
                )}

                {/* Show Google SSO first for both sign-up and sign-in */}
                {authMode === 'signup' && !showEmailSignup ? (
                  <div className="space-y-4">
                    {/* Google Sign Up - Primary */}
                    <button
                      onClick={handleGoogleAuth}
                      disabled={authLoading}
                      className="w-full bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 px-6 py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign up with Google
                    </button>

                    {/* Divider */}
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-slate-900 text-gray-400">Or</span>
                      </div>
                    </div>

                    {/* Email Sign Up - Secondary Option */}
                    <button
                      onClick={() => setShowEmailSignup(true)}
                      className="w-full bg-white/5 hover:bg-white/10 border border-white/20 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      Sign up with Email
                    </button>
                  </div>
                ) : authMode === 'signin' ? (
                  <div className="space-y-4">
                    {/* Google Sign In - Primary Option */}
                    <button
                      onClick={handleGoogleAuth}
                      disabled={authLoading}
                      className="w-full bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign in with Google
                    </button>

                    {/* Divider */}
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-slate-900 text-gray-400">Or sign in with email</span>
                      </div>
                    </div>

                    {/* Email Sign In Form */}
                    <form onSubmit={handleEmailAuth} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="your@email.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Password *
                        </label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="••••••••"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={authLoading}
                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {authLoading ? 'Signing in...' : 'Sign In'}
                      </button>
                    </form>
                  </div>
                ) : (
                  <form onSubmit={handleEmailAuth} className="space-y-4">
                  {authMode === 'signup' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                          placeholder="Doe"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                      placeholder="••••••••"
                    />
                    {authMode === 'signup' && formData.password && (
                      <div className="mt-3 space-y-2 bg-white/5 rounded-lg p-3 border border-white/10">
                        <p className="text-xs font-semibold text-gray-300 mb-2">Password Requirements:</p>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            {passwordValidation.minLength ? (
                              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            <span className={`text-xs ${passwordValidation.minLength ? 'text-emerald-400' : 'text-gray-400'}`}>
                              At least 8 characters
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasUpperCase ? (
                              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            <span className={`text-xs ${passwordValidation.hasUpperCase ? 'text-emerald-400' : 'text-gray-400'}`}>
                              One uppercase letter
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasLowerCase ? (
                              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            <span className={`text-xs ${passwordValidation.hasLowerCase ? 'text-emerald-400' : 'text-gray-400'}`}>
                              One lowercase letter
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passwordValidation.hasNumber ? (
                              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            <span className={`text-xs ${passwordValidation.hasNumber ? 'text-emerald-400' : 'text-gray-400'}`}>
                              One number
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {authMode === 'signup' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleFormChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:border-emerald-400 focus:outline-none"
                        placeholder="••••••••"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {authLoading ? (authMode === 'signin' ? 'Signing in...' : 'Creating Account...') : (authMode === 'signin' ? 'Sign In' : 'Create Account')}
                  </button>

                  {/* Back to Google SSO */}
                  {authMode === 'signup' && showEmailSignup && (
                    <button
                      type="button"
                      onClick={() => setShowEmailSignup(false)}
                      className="w-full text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      ← Back to sign-up options
                    </button>
                  )}
                  {authMode === 'signin' && showEmailSignin && (
                    <button
                      type="button"
                      onClick={() => setShowEmailSignin(false)}
                      className="w-full text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      ← Back to sign-in options
                    </button>
                  )}
                </form>
                )}

                {/* Switch Mode Link */}
                <div className="mt-6 text-center">
                  <p className="text-gray-400">
                    {authMode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                    <button
                      onClick={() => {
                        setAuthMode(authMode === 'signin' ? 'signup' : 'signin')
                        setShowEmailSignup(false)
                        setShowEmailSignin(false)
                      }}
                      className="text-emerald-400 hover:text-emerald-300 font-bold"
                    >
                      {authMode === 'signin' ? 'Sign Up' : 'Sign In'}
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Verification Notice Modal */}
        {showEmailVerificationNotice && (
          <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/70' : 'bg-black/50'} backdrop-blur-md flex items-center justify-center p-4 z-50`}>
            <div className={`${themeClasses.bg} rounded-2xl shadow-2xl max-w-md w-full border ${themeClasses.border}`}>
              {/* Header */}
              <div className={`p-6 border-b ${themeClasses.border}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500/30 flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className={`text-xl font-bold ${themeClasses.text}`}>Verify Your Email</h2>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className={`bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4`}>
                  <p className={`${themeClasses.text} text-sm`}>
                    Account created successfully! 🎉
                  </p>
                </div>

                <div className="space-y-3">
                  <p className={themeClasses.text}>
                    We've sent a verification email to:
                  </p>
                  <p className="text-emerald-400 font-semibold break-all">
                    {verificationEmail}
                  </p>
                  <p className={`${themeClasses.textMuted} text-sm`}>
                    Please check your inbox and click the verification link to activate your account. You may need to check your spam folder.
                  </p>
                </div>

                <div className={`${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-300'} rounded-lg p-4 border`}>
                  <p className={`${isDarkMode ? 'text-amber-200' : 'text-amber-800'} text-xs`}>
                    <strong className={`${isDarkMode ? 'text-amber-300' : 'text-amber-900'}`}>Important:</strong> You can browse tournament info, but you must verify your email to access <strong>brackets, schedule, and live scores</strong>.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 border-t ${themeClasses.border}`}>
                <button
                  onClick={() => setShowEmailVerificationNotice(false)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Got It
                </button>
              </div>
            </div>
          </div>
        )}

        <TournamentView
          data={tournamentData}
          colorTheme={colorTheme}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          currentUser={currentUser}
          registrationCounts={registrationCounts}
          onRefresh={() => fetchTournamentData(false)}
          onAuthRequired={() => {
            setAuthMode('signin')
            setShowAuthModal(true)
          }}
          onRegister={(category) => {
            setSelectedCategory(category)
            setShowRegistrationModal(true)
          }}
          onResendVerification={handleResendVerification}
          resendLoading={resendLoading}
          resendSuccess={resendSuccess}
          resendError={resendError}
          categoryCache={categoryCache}
          fetchCategoryData={fetchCategoryData}
          onCategoryChange={(id) => {
            setCurrentCategoryId(id)
            currentCategoryIdRef.current = id
          }}
        />

        {/* Registration Modal */}
        <RegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false)
            setSelectedCategory(null)
          }}
          category={selectedCategory}
          tournament={tournamentData?.tournament}
          currentUser={currentUser}
          userProfile={userProfile}
          themeClasses={themeClasses}
          colorTheme={colorTheme}
          isDarkMode={isDarkMode}
        />
      </>
    )
  }

  // Error state - show retry option
  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${themeClasses.gradient} flex items-center justify-center p-4 transition-colors duration-300`}>
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <Trophy className={`w-16 h-16 ${colorTheme.text.primary} mx-auto mb-4`} />
            <h1 className={`text-4xl font-black mb-2 ${themeClasses.text}`}>
              Tournament Viewer
            </h1>
            <p className={`text-lg ${themeClasses.textMuted}`}>
              {colorTheme.ribbon} Unable to load tournament data
            </p>
          </div>

          <div className={`glass rounded-2xl shadow-2xl p-8`}>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Connection Error
              </h2>
              <div className="bg-red-500/10 border-2 border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400 text-sm font-semibold">
                  {error}
                </p>
              </div>
              <p className="text-gray-400 text-sm">
                Unable to fetch tournament data from the server. Please check your connection and try again.
              </p>
            </div>

            <button
              onClick={() => window.location.reload()}
              className={`w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-lg`}
            >
              <Trophy className="w-5 h-5" />
              Retry
            </button>
          </div>

          <div className={`text-center mt-6 text-sm ${themeClasses.textMuted}`}>
            <p>{colorTheme.ribbon} Powered by Sports Control Hub</p>
          </div>
        </div>
      </div>
    )
  }

}

function App() {
  const [tournamentStatus, setTournamentStatus] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)

  // Fetch tournament status first to determine if auth is needed
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(TOURNAMENT_DATA_URL)
        const data = await response.json()
        setTournamentStatus(data?.tournament?.status)
      } catch (error) {
        console.error('Error fetching tournament status:', error)
      } finally {
        setInitialLoading(false)
      }
    }
    fetchStatus()
  }, [])

  // Only enable auth when tournament status requires it (not upcoming)
  const enableAuth = tournamentStatus !== 'upcoming'

  if (initialLoading) {
    return null // Or a minimal loading indicator
  }

  return (
    <AuthProvider enableAuth={enableAuth}>
      <AppContent />
    </AuthProvider>
  )
}

export default App
